import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import ScopeSearchModal from '../../components/ScopeSearchModal';
import {
  getSupervisorPermissions,
  setSupervisorPermissions,
  listSupervisors,
} from '../../api/supervisorPermissions';
import './SupervisorPermissionsPage.css';

const SupervisorPermissionsPage = () => {
  const [supervisors, setSupervisors] = useState([]);
  const [supId, setSupId] = useState('');
  // Champ texte + 🔍 (comme les autres filtres de portee, plus de <select>) :
  // `supText` = ce qui est AFFICHE/tape (numero seq ou username), `supId` =
  // l'id interne resolu, seul utilise pour les appels API.
  const [supText, setSupText] = useState('');
  const [supName, setSupName] = useState('');
  const [supLookupError, setSupLookupError] = useState('');
  const [showSupModal, setShowSupModal] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [denied, setDenied] = useState(new Set());
  const [menuCatalog, setMenuCatalog] = useState([]);
  const [menuKeys, setMenuKeys] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    listSupervisors()
      .then((rows) => setSupervisors(rows))
      .catch(() => setError('Impossible de charger les superviseurs.'));
  }, []);

  useEffect(() => {
    if (!supId) { setCatalog(null); setMenuCatalog([]); return; }
    setLoading(true);
    setMsg('');
    getSupervisorPermissions(supId)
      .then((d) => {
        setCatalog(d.catalog);
        setDenied(new Set(d.denied));
        setMenuCatalog(d.menu_catalog || []);
        setMenuKeys(new Set(d.menu_keys || []));
      })
      .catch(() => setError('Chargement des permissions impossible.'))
      .finally(() => setLoading(false));
  }, [supId]);

  const pickSupervisor = (s) => {
    setSupId(String(s.id));
    setSupText(String(s.seq ?? ''));
    setSupName(`${s.display_name || s.name}${s.username ? ` (${s.username})` : ''}`);
    setSupLookupError('');
    setError('');
  };

  // Quitter le champ : resout le numero (seq) ou le username tape parmi les
  // superviseurs de la company (deja charges).
  const resolveSupervisorText = () => {
    const t = supText.trim();
    if (!t) { setSupId(''); setSupName(''); setSupLookupError(''); return; }
    const found = supervisors.find((s) => String(s.seq) === t)
      || supervisors.find((s) => (s.username || '').toLowerCase() === t.toLowerCase());
    if (found) {
      pickSupervisor(found);
    } else {
      setSupId('');
      setSupName('');
      setSupLookupError('Superviseur introuvable');
    }
  };

  const key = (m, a) => `${m}.${a}`;
  const allowed = (m, a) => !denied.has(key(m, a));

  const toggle = (m, a) => {
    setDenied((prev) => {
      const next = new Set(prev);
      const k = key(m, a);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const toggleMenuKey = (menuKey) => {
    setMenuKeys((prev) => {
      const next = new Set(prev);
      if (next.has(menuKey)) next.delete(menuKey); else next.add(menuKey);
      return next;
    });
  };

  const toggleMenuSection = (items, checkAll) => {
    setMenuKeys((prev) => {
      const next = new Set(prev);
      items.forEach((it) => (checkAll ? next.add(it.key) : next.delete(it.key)));
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setMsg('');
    setError('');
    try {
      const res = await setSupervisorPermissions(supId, [...denied], [...menuKeys]);
      setDenied(new Set(res.denied));
      setMenuKeys(new Set(res.menu_keys));
      setMsg('Permissions enregistrées.');
    } catch (err) {
      setError('Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <p className="spp-breadcrumb">
        Ruta: <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/parametres" className="bc-link">Parametres</Link>
        {' >> '}
        <span className="bc-current">Permissions superviseur</span>
      </p>

      {supervisors.length === 0 ? (
        <p className="spp-empty">
          Aucun superviseur. Crée-en un d'abord dans{' '}
          <Link to="/parametres/superviseurs" className="bc-link">Parametres &gt; Superviseurs</Link>.
        </p>
      ) : (
        <div className="spp-row">
          <label>Superviseur :</label>
          <input
            className="spp-input"
            type="text"
            value={supText}
            onChange={(e) => { setSupText(e.target.value); setSupLookupError(''); }}
            onFocus={(e) => e.target.select()}
            onBlur={resolveSupervisorText}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); resolveSupervisorText(); } }}
          />
          <span className="spp-icon-btn" onClick={() => setShowSupModal(true)}>🔍</span>
          {supLookupError
            ? <span className="spp-lookup-err">{supLookupError}</span>
            : supName && <span className="spp-sup-name">{supName}</span>}
        </div>
      )}

      {showSupModal && (
        <ScopeSearchModal
          filter="Supervisor"
          onSelect={(id) => {
            const s = supervisors.find((x) => String(x.id) === String(id));
            if (s) pickSupervisor(s);
          }}
          onClose={() => setShowSupModal(false)}
        />
      )}

      {error && <p className="spp-error">{error}</p>}
      {loading && <p>Chargement…</p>}

      {catalog && !loading && (
        <>
          <p className="spp-hint">
            Coché = autorisé. Décoche pour <strong>retirer</strong> une action à ce superviseur
            (par défaut il a tout).
          </p>
          <table className="spp-table">
            <thead>
              <tr>
                <th>Modèle</th>
                {catalog.actions.map((a) => <th key={a.key}>{a.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {catalog.models.map((m) => (
                <tr key={m.key}>
                  <td>{m.label}</td>
                  {catalog.actions.map((a) => (
                    <td key={a.key} className="spp-cell">
                      <input
                        type="checkbox"
                        checked={allowed(m.key, a.key)}
                        onChange={() => toggle(m.key, a.key)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          <h3 className="spp-section-title">Pages accessibles (Central, Rapports, …)</h3>
          <p className="spp-hint">
            Décoché = <strong>inaccessible</strong> pour ce superviseur (par défaut il n'a
            aucune de ces pages — coche celles à lui accorder).
          </p>
          <div className="spp-catalog">
            {menuCatalog.map((section) => (
              <div key={section.section} className="spp-menu-section">
                <div className="spp-menu-section-header">
                  <strong>{section.section}</strong>
                  <span className="spp-menu-section-actions">
                    <button type="button" onClick={() => toggleMenuSection(section.items, true)}>
                      Tout cocher
                    </button>
                    <button type="button" onClick={() => toggleMenuSection(section.items, false)}>
                      Tout decocher
                    </button>
                  </span>
                </div>
                <div className="spp-menu-section-items">
                  {section.items.map((item) => (
                    <label key={item.key} className="spp-menu-item-checkbox">
                      <input
                        type="checkbox"
                        checked={menuKeys.has(item.key)}
                        onChange={() => toggleMenuKey(item.key)}
                      />
                      {item.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {msg && <p className="spp-ok">{msg}</p>}
          <button className="spp-save" onClick={save} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </>
      )}
    </Layout>
  );
};

export default SupervisorPermissionsPage;
